// The flat workflow definition (channel processing spec 3.3) and its drawing (3.8), in the
// browser. Mirrors app/models/workflow/definition.rb: the same transitive reduction and the same
// set-boundary decomposition, checked against the same fixtures in spec/fixtures/workflow_definitions.
// No imports; the designer's object shapes are produced here but the library is never required.
//
// A step is `{ kind, ...fields, needs: [names] }`. A drawing item is `{ step: name }` or
// `{ parallel: [lane, ...] }` where each lane is a list of items. Only Ruby hashes definitions;
// this side compares canonical structures.

export const KINDS = ["triage", "transmit"];
const NAME_FORMAT = /^[a-z][a-z0-9_]{0,39}$/;

export class UndrawableError extends Error {
  constructor(steps) {
    super(`Steps ${steps.join(", ")} cannot be drawn as lanes: a step depends on two lanes at once.`);
    this.steps = steps;
  }
}

const isScalar = (v) => v === null || typeof v !== "object";

// Sorted keys throughout; lists of scalars sorted. The same form Ruby hashes.
export function canonicalize(value) {
  if (Array.isArray(value)) {
    const items = value.map(canonicalize);
    return items.every(isScalar) ? items.slice().sort((a, b) => String(a).localeCompare(String(b))) : items;
  }
  if (value && typeof value === "object") {
    const out = {};
    Object.keys(value).sort().forEach((k) => { out[k] = canonicalize(value[k]); });
    return out;
  }
  return value;
}

export const canonicalJson = (value) => JSON.stringify(canonicalize(value));
export const canonicalEqual = (a, b) => canonicalJson(a) === canonicalJson(b);

export const stepNames = (def) => Object.keys((def && def.steps) || {});

function needsOf(def, name) {
  const step = def.steps[name] || {};
  return (Array.isArray(step.needs) ? step.needs : []).map(String).filter((m) => Object.hasOwn(def.steps, m));
}

function ancestorSet(def, name) {
  const seen = new Set();
  const stack = needsOf(def, name);
  while (stack.length) {
    const n = stack.pop();
    if (seen.has(n)) continue;
    seen.add(n);
    stack.push(...needsOf(def, n));
  }
  return seen;
}

// name => Set of every step reachable through needs, computed once per definition pass.
function ancestorMap(def) {
  const map = new Map();
  stepNames(def).forEach((n) => map.set(n, ancestorSet(def, n)));
  return map;
}

export function ancestors(def, name) {
  return [...ancestorSet(def, name)];
}

export function topologicalOrder(def) {
  const names = stepNames(def);
  const indegree = {};
  const succ = {};
  names.forEach((n) => { indegree[n] = needsOf(def, n).length; succ[n] = []; });
  names.forEach((n) => needsOf(def, n).forEach((m) => succ[m].push(n)));
  const ready = names.filter((n) => indegree[n] === 0);
  const out = [];
  while (ready.length) {
    const n = ready.shift();
    out.push(n);
    succ[n].forEach((m) => { indegree[m] -= 1; if (indegree[m] === 0) ready.push(m); });
  }
  return out.length === names.length ? out : null;
}

// For each step, its needs minus any need reachable from another of its needs.
export function reducedNeeds(def, anc = ancestorMap(def)) {
  const out = {};
  stepNames(def).forEach((n) => {
    const direct = needsOf(def, n);
    out[n] = direct.filter((m) => !direct.some((o) => o !== m && anc.get(o).has(m))).sort();
  });
  return out;
}

// The same definition with every needs list reduced; comparisons use this form.
export function reduced(def) {
  const needs = reducedNeeds(def);
  const steps = {};
  stepNames(def).forEach((n) => {
    const { needs: _ignored, ...rest } = def.steps[n];
    steps[n] = needs[n].length ? { ...rest, needs: needs[n] } : rest;
  });
  return { steps };
}

// ---- drawing: set-boundary decomposition (see the Ruby Graph class for the rule) ----

function components(set, reduced) {
  const seen = new Set();
  const out = [];
  set.forEach((n) => {
    if (seen.has(n)) return;
    const comp = [];
    const stack = [n];
    while (stack.length) {
      const x = stack.pop();
      if (seen.has(x) || !set.includes(x)) continue;
      seen.add(x);
      comp.push(x);
      stack.push(...reduced[x], ...set.filter((m) => reduced[m].includes(x)));
    }
    out.push(set.filter((m) => comp.includes(m)));
  });
  return out;
}

function boundary(left, right, exits, reduced) {
  const leftExits = left.filter((n) => !left.some((m) => reduced[m].includes(n)));
  if (leftExits.slice().sort().join() !== exits.slice().sort().join()) return false;
  const entries = right.filter((n) => !reduced[n].some((m) => right.includes(m)));
  const sortedExits = exits.slice().sort().join();
  return entries.every((n) => reduced[n].slice().sort().join() === sortedExits) &&
    right.filter((n) => !entries.includes(n)).every((n) => !reduced[n].some((m) => left.includes(m)));
}

function smallestBoundary(set, reduced, anc) {
  const candidates = [];
  set.forEach((n) => {
    const key = reduced[n].join();
    if (reduced[n].length && !candidates.some((c) => c.join() === key)) candidates.push(reduced[n]);
  });
  let best = null;
  candidates.forEach((exits) => {
    const closure = new Set(exits);
    exits.forEach((e) => anc.get(e).forEach((a) => closure.add(a)));
    const left = set.filter((n) => closure.has(n));
    const right = set.filter((n) => !closure.has(n));
    if (!right.length || !boundary(left, right, exits, reduced)) return;
    if (!best || left.length < best.length) best = left;
  });
  return best;
}

function itemFor(set, reduced, anc) {
  if (set.length === 1) return { step: set[0] };
  const lanes = components(set, reduced);
  if (lanes.length === 1) throw new UndrawableError(set.slice().sort());
  lanes.sort((a, b) => a.slice().sort()[0].localeCompare(b.slice().sort()[0]));
  return { parallel: lanes.map((lane) => decompose(lane, reduced, anc)) };
}

function decompose(set, reduced, anc) {
  const items = [];
  let rest = set.slice();
  const local = {};
  set.forEach((n) => { local[n] = reduced[n].filter((m) => set.includes(m)); });
  while (rest.length) {
    const left = smallestBoundary(rest, local, anc);
    if (!left) { items.push(itemFor(rest, local, anc)); break; }
    items.push(itemFor(left, local, anc));
    rest = rest.filter((n) => !left.includes(n));
  }
  return items;
}

// The abstract drawing; throws UndrawableError with the step names for the N shape.
export function toItems(def) {
  const names = stepNames(def);
  if (!names.length) return [];
  if (topologicalOrder(def) === null) throw new Error("The steps form a cycle");
  const anc = ancestorMap(def);
  return decompose(names, reducedNeeds(def, anc), anc);
}

// The designer's definition object. `uid` supplies ids (the designer's Uid.next in the app, a
// counter in tests). Ids and component types are never stored.
export function toDrawing(def, uid) {
  const task = (name) => {
    const { kind, needs: _ignored, ...properties } = def.steps[name];
    return { id: uid(), componentType: "task", type: kind, name, properties };
  };
  const lane = (items) => ({ id: uid(), componentType: "container", type: "lane", name: "lane", properties: {}, sequence: items.map(item) });
  const item = (it) => {
    if (it.step) return task(it.step);
    const children = it.parallel.map((l) => (l.length === 1 && l[0].step ? task(l[0].step) : lane(l)));
    return { id: uid(), componentType: "launchPad", type: "parallel", name: "parallel", properties: {}, sequence: children };
  };
  return { properties: {}, sequence: toItems(def).map(item) };
}

export function slugify(name) {
  let s = String(name || "").toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "");
  if (!/^[a-z]/.test(s)) s = `s_${s}`;
  return s.slice(0, 40);
}

// The designer's definition back to the flat form. A step's needs is the exit set of what
// precedes it: the previous step in its lane, or the last steps of every lane of a preceding block.
// Names are slugified with a numeric suffix on a collision. `ids`, when given, receives name => id.
export function toDefinition(drawing, ids) {
  const steps = {};
  const used = new Set();
  const unique = (n) => {
    const base = slugify(n) || "step";
    let k = base;
    let i = 2;
    while (used.has(k)) k = `${base.slice(0, 40 - String(i).length - 1)}_${i++}`;
    used.add(k);
    return k;
  };
  const walk = (sequence, entry) => {
    let exits = entry;
    (sequence || []).forEach((step) => {
      if (step.componentType === "launchPad") {
        const out = [];
        (step.sequence || []).forEach((child) => {
          out.push(...walk(child.componentType === "container" ? child.sequence : [child], exits));
        });
        exits = out;
      } else if (step.componentType === "container") {
        exits = walk(step.sequence, exits);
      } else {
        const name = unique(step.name);
        if (ids) ids[name] = step.id;
        steps[name] = { kind: step.type, ...(step.properties || {}) };
        if (exits.length) steps[name].needs = exits.slice().sort();
        exits = [name];
      }
    });
    return exits;
  };
  walk(drawing && drawing.sequence, []);
  return { steps };
}

// ---- selections and checks ----

export const triageSteps = (def) => stepNames(def).filter((n) => def.steps[n].kind === "triage");
export const unitsPerReport = (def) => triageSteps(def).filter((n) => def.steps[n].engine === "ai").length;

export function producedBefore(def, name) {
  const keys = new Set();
  ancestors(def, name).forEach((a) => { const s = def.steps[a]; if (s.kind === "triage" && s.task) keys.add(s.task); });
  return [...keys];
}

// Every problem the editor can see without the server: a transmit step without an integration or
// with a condition nothing produces. [{ step, problem }].
export function definitionProblems(def) {
  const out = [];
  stepNames(def).forEach((name) => {
    const step = def.steps[name];
    if (step.kind === "transmit" && !(step.integration && step.integration.id)) out.push({ step: name, problem: "Choose an integration." });
    const problem = conditionProblem(def, name);
    if (problem) out.push({ step: name, problem });
  });
  return out;
}

// The sentence the editor shows on a transmit step, or null.
export function conditionProblem(def, name) {
  const step = def.steps[name];
  if (!step || step.kind !== "transmit") return null;
  const conditions = step.only_when;
  if (conditions && typeof conditions === "object" && Object.keys(conditions).length) {
    const produced = producedBefore(def, name);
    const missing = Object.keys(conditions).find((k) => !produced.includes(k));
    if (missing) return `Nothing before this step produces ${missing}.`;
  }
  return null;
}

// ---- change summary against another definition (both reduced) ----

const inline = (v) => {
  if (v === undefined || v === null) return "none";
  if (Array.isArray(v)) return v.map(inline).join(", ");
  if (typeof v === "object") return Object.keys(v).map((k) => `${k} ${inline(v[k])}`).join(" ");
  return String(v);
};

function describe(name, step) {
  const parts = Object.keys(step).filter((k) => k !== "kind" && k !== "needs").map((k) => `${k} ${inline(step[k])}`);
  return `${name} (${step.kind}${parts.length ? `, ${parts.join(", ")}` : ""})`;
}

export function summarize(from, to) {
  const a = reduced(from || { steps: {} });
  const b = reduced(to || { steps: {} });
  const added = stepNames(b).filter((n) => !(n in a.steps));
  const removed = stepNames(a).filter((n) => !(n in b.steps));
  const changed = stepNames(b).filter((n) => n in a.steps && !canonicalEqual(a.steps[n], b.steps[n]));
  const sentences = [];
  added.forEach((n) => sentences.push(`Added ${describe(n, b.steps[n])}`));
  changed.forEach((n) => {
    const keys = [...new Set([...Object.keys(a.steps[n]), ...Object.keys(b.steps[n])])].sort()
      .filter((k) => !canonicalEqual(a.steps[n][k] === undefined ? null : a.steps[n][k], b.steps[n][k] === undefined ? null : b.steps[n][k]));
    sentences.push(`Changed ${n}: ${keys.map((k) => `${k} ${inline(a.steps[n][k])} to ${inline(b.steps[n][k])}`).join(", ")}`);
  });
  removed.forEach((n) => sentences.push(`Removed ${n}`));
  return { added, removed, changed, sentences, sentence: sentences.length ? `${sentences.join(". ")}.` : "" };
}

// ---- text view ----

const inlineJson = (v) => (Array.isArray(v) ? `[${v.map(inlineJson).join(", ")}]` : JSON.stringify(v));

export function formatFlat(def) {
  const names = stepNames(def);
  if (!names.length) return '{\n  "steps": {}\n}';
  const width = Math.max(...names.map((n) => n.length)) + 3;
  const lines = names.map((n) => {
    const d = def.steps[n];
    const body = Object.keys(d).map((k) => `${JSON.stringify(k)}: ${inlineJson(d[k])}`).join(", ");
    return `    ${`${JSON.stringify(n)}:`.padEnd(width)} { ${body} }`;
  });
  return `{\n  "steps": {\n${lines.join(",\n")}\n  }\n}`;
}

// Parses the text view; throws with a message on bad JSON, a missing steps object, a bad name,
// an unknown kind, a missing need, a cycle, or an undrawable graph.
export function parseFlat(text) {
  let doc;
  try {
    doc = JSON.parse(text);
  } catch (e) {
    throw new Error(`Invalid JSON: ${e.message}`);
  }
  if (!doc || typeof doc !== "object" || Array.isArray(doc) || !doc.steps || typeof doc.steps !== "object" || Array.isArray(doc.steps)) {
    throw new Error('Expected a top-level "steps" object');
  }
  const names = Object.keys(doc.steps);
  names.forEach((n) => {
    const d = doc.steps[n];
    if (!d || typeof d !== "object" || Array.isArray(d)) throw new Error(`Step "${n}" must be an object`);
    if (!NAME_FORMAT.test(n)) throw new Error(`Step name "${n}" must be lowercase letters, digits, and underscores, starting with a letter`);
    if (!KINDS.includes(d.kind)) throw new Error(`Unknown step kind on "${n}": ${d.kind}`);
    if (d.needs !== undefined && !Array.isArray(d.needs)) throw new Error(`Step "${n}": needs must be a list of step names`);
    (d.needs || []).forEach((m) => {
      if (!Object.hasOwn(doc.steps, m)) throw new Error(`"${n}" needs "${m}", which does not exist`);
      if (m === n) throw new Error(`"${n}" cannot need itself`);
    });
  });
  toItems({ steps: doc.steps });
  return { steps: doc.steps };
}
